/**
 * @ComponentFor VideoProductsBlockViewModel
 */
import React, { useState } from 'react';
import { styled } from '@glitz/react';
import { default as VideoProductsBlockType } from './VideoProductsBlockViewModel.type';
import { Play12x12 } from 'Shared/Icons/Play';
import Video from 'Shared/Video/Video';
import { HEIGHT as DESKTOP_HEADER_HEIGHT } from 'SiteLayout/Header/DesktopHeader';
import { BLACK_TOP_MOBILE_HEIGHT, BLACK_TOP_DESKTOP_HEIGHT } from 'SiteLayout/Header';
import { HEIGHT as MOBILE_HEIGHT } from 'SiteLayout/Header/MobileHeader';
import { ButtonHeight, ButtonTheme, SolidButton } from 'Shared/TextButtons';
import { Plus12x12 } from 'Shared/Icons/Plus';
import connect from 'Shared/connect';
import { createGridClasses, createOffsetGridClassLarge } from 'Shared/grid';
import { GridWrap } from 'Shared/PageLayout';
import { selectDeviceImage } from 'Shared/utils';
import { isCompact as functionIsCompact } from 'Shared/Viewport';
import { H2, ResponsiveSize } from 'Shared/SharedComponents/atoms/Typography';
import * as style from 'Shared/Style';
import QuickShopProductModal from 'Shared/QuickShopProductModal';
import { Preset } from 'Shared/image-sizes';

type PropTypes = VideoProductsBlockType & {
};

const Wrap = styled.div({
  width: '100%',
  height: `calc(100vh - ${MOBILE_HEIGHT + BLACK_TOP_MOBILE_HEIGHT}px)`,
  ['@media ' + style.mediaMinLarge]: {
    height: `calc(100vh - ${DESKTOP_HEADER_HEIGHT + BLACK_TOP_DESKTOP_HEIGHT}px)`,
  },
})

const TextElement = styled.span({
  display: 'block',
  width: '100%',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '100%',
  textAlign: 'center',
  textTransform: 'uppercase',
});

const GridWrapExtended = styled(GridWrap, {
  transition: {
    property: 'opacity',
    timingFunction: 'ease-in-out',
    duration: '0.3s',
  },
  height: '100%',
  position: 'absolute',
  top: 0,
})

const Label = styled(TextElement, {
  alignSelf: 'end',
  fontSize: '14px',
  letterSpacing: '0.01em',
  marginBottom: '16px',
  color: style.colors.monochrome.white,
  zIndex: 1,
})

const Heading = styled(H2, {
  alignSelf: 'start',
  color: style.colors.monochrome.white,
  zIndex: 1,
  textAlign: 'center',
})

const ButtonWrap = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'end',
  paddingBottom: '40px',
  height: '100%',
})

const CenterContent = styled.div({
  textAlign: 'center',
})

const Plus = styled(Plus12x12, {
  marginLeft: '8px'
})

const Play = styled(Play12x12, {
  marginLeft: '8px'
})

export const VideoProductsBlock = connect(
  (state) => ({
    currentBreakpoint: state.currentBreakpoint,
  })
)((props: PropTypes & { currentBreakpoint: number }) => {
  const [videoStarted, setVideoStarted] = useState(false);
  const [modalIsShown, setModalIsShown] = useState<boolean>(false);
  const isMobile = functionIsCompact(props.currentBreakpoint);

  const {
    imagePreview,
    imagePreviewSmallScreen,
    productItems,
    heading,
    label,
    seeProductsButtonText = '',
    playButtonText = '',
  } = props;

  return (
    <>
      <Wrap>
        <Video
          videoSources={props.videoSources}
          previewImage={selectDeviceImage(imagePreview, imagePreviewSmallScreen, isMobile)}
          previewImagePreset={Preset.FullWidth}
          previewVideoSources={props.videoPreviewSources}
          isCompact={isMobile}
          videoStarted={videoStarted}
          stopVideo={() => setVideoStarted(false)}
          buttonsInControls={
            productItems?.length > 0
              ? [
                {
                  text: isMobile
                    ? `(${productItems.length})`
                    : `${seeProductsButtonText} (${productItems.length})`,
                  onClick: () => setModalIsShown(true),
                },
              ]
              : []
          }
        >
          <GridWrapExtended className="grid">
            <Label className={createGridClasses(12, 4, createOffsetGridClassLarge(5))}>
              {label}
            </Label>
            <Heading
              className={createGridClasses(12, 8, createOffsetGridClassLarge(3))}
              fontSize={ResponsiveSize.D96_M40}
            >
              {heading}
            </Heading>
            <ButtonWrap className={createGridClasses(12, 12)}>
              <CenterContent>
                <SolidButton
                  text={playButtonText}
                  onClick={() => setVideoStarted(true)}
                  theme={ButtonTheme.WhiteBorderToBlack}
                  iconAfter={<Play />}
                  height={ButtonHeight.Big}
                />
                {productItems?.length > 0 && (
                  <SolidButton
                    text={`${isMobile ? '' : `${seeProductsButtonText} `}(${productItems.length})`}
                    onClick={() => setModalIsShown(true)}
                    iconAfter={<Plus />}
                    height={ButtonHeight.Big}
                    css={{ marginLeft: '8px' }}
                  />
                )}
              </CenterContent>
            </ButtonWrap>
          </GridWrapExtended>
        </Video>
      </Wrap>
      <QuickShopProductModal isMobile={isMobile} modalIsShown={modalIsShown} hideModal={() => setModalIsShown(false)} products={productItems} />
    </>
  );
});

export default VideoProductsBlock;
